import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X, Check } from 'lucide-react';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
  FloatingPortal,
} from '@floating-ui/react';

interface MultiSelectProps {
  options: string[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  displayMode?: 'tags' | 'count' | 'list';
  maxDisplayTags?: number;
  searchable?: boolean;
  showSelected?: boolean;
  allowCustom?: boolean;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select options...',
  disabled = false,
  className = '',
  displayMode = 'tags',
  maxDisplayTags = 2,
  searchable = true,
  showSelected = true,
  allowCustom = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const referenceRef = useRef<HTMLElement | null>(null);

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    strategy: 'fixed',
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            '--dropdown-max-height': `${Math.min(availableHeight - 8, 224)}px`,
          } as React.CSSProperties);
        },
      }),
    ],
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const referenceNode = referenceRef.current;
      const floatingNode = refs.floating.current;

      if (referenceNode?.contains(target) || floatingNode?.contains(target)) return;
      if (containerRef.current && !containerRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [refs.floating]);

  useEffect(() => {
    if (isOpen && searchable) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    } else if (searchQuery) {
      setSearchQuery('');
    }
  }, [isOpen, searchQuery, searchable]);

  const toggleOption = (option: string) => {
    if (value.includes(option)) {
      onChange(value.filter((v) => v !== option));
    } else {
      onChange([...value, option]);
    }
  };

  const addCustomOption = (customValue: string) => {
    if (customValue.trim() && !value.includes(customValue.trim())) {
      onChange([...value, customValue.trim()]);
      setSearchQuery('');
    }
  };

  const removeOption = (option: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== option));
  };

  const setReferenceNode = (node: HTMLElement | null) => {
    referenceRef.current = node;
    refs.setReference(node);
  };

  const renderSelectedDisplay = () => {
    if (value.length === 0) {
      return <span className="text-gray-400">{placeholder}</span>;
    }

    switch (displayMode) {
      case 'count':
        return (
          <span className="text-gray-700 font-medium">
            {value.length} {value.length === 1 ? 'item' : 'items'} selected
          </span>
        );

      case 'list':
        return <span className="text-gray-700">{value.join(', ')}</span>;

      case 'tags':
      default: {
        const displayTags = value.slice(0, maxDisplayTags);
        const remaining = value.length - maxDisplayTags;

        return (
          <>
            {displayTags.map((item) => (
              <span
                key={item}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-secondary/10 text-secondary text-sm rounded-md border border-secondary/20"
              >
                {item}
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => removeOption(item, e)}
                    className="hover:text-secondary"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </span>
            ))}
            {remaining > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-600 text-sm rounded-md border border-gray-300">
                +{remaining} more
              </span>
            )}
          </>
        );
      }
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        ref={setReferenceNode}
        className={`input-field cursor-pointer flex items-center justify-between min-h-[42px] transition-all ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : ''
        } ${isOpen ? 'border-2 border-primary ring-2 ring-primary ring-opacity-20' : ''}`}
      >
        <div className="flex flex-wrap gap-1 flex-1">
          {searchable && isOpen ? (
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              className="bg-transparent outline-none text-sm text-gray-700 placeholder:text-gray-400 w-full"
              onClick={(e) => e.stopPropagation()}
              onFocus={() => !disabled && setIsOpen(true)}
            />
          ) : showSelected ? (
            renderSelectedDisplay()
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
        </div>
        <ChevronDown
          className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
        />
      </div>

      {isOpen && !disabled && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className="z-[1040] bg-white border-2 rounded-lg shadow-lg overflow-hidden"
          >
            <div
              className="overflow-auto"
              style={{ maxHeight: 'var(--dropdown-max-height, 224px)' }}
            >
              {options.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">No options available</div>
              ) : (
                (() => {
                  const q = searchable ? searchQuery.trim().toLowerCase() : '';
                  const filtered = q
                    ? options.filter((opt) => opt.toLowerCase().includes(q))
                    : options;

                  const showCustomOption =
                    allowCustom &&
                    q &&
                    !options.includes(searchQuery.trim()) &&
                    !value.includes(searchQuery.trim());

                  if (filtered.length === 0 && !showCustomOption) {
                    return (
                      <div className="px-3 py-2 text-sm text-gray-500">No results</div>
                    );
                  }

                  return (
                    <>
                      {filtered.map((option) => (
                        <div
                          key={option}
                          onClick={() => toggleOption(option)}
                          className={`px-3 py-2 cursor-pointer hover:bg-gray-50 flex items-center justify-between ${
                            value.includes(option) ? 'bg-secondary/10' : ''
                          }`}
                        >
                          <span
                            className={
                              value.includes(option) ? 'text-secondary font-medium' : ''
                            }
                          >
                            {option}
                          </span>
                          {value.includes(option) && (
                            <div className="w-5 h-5 bg-secondary text-white rounded flex items-center justify-center">
                              <Check className="w-3 h-3" strokeWidth={3} />
                            </div>
                          )}
                        </div>
                      ))}
                      {showCustomOption && (
                        <div
                          onClick={() => addCustomOption(searchQuery)}
                          className="px-3 py-2 cursor-pointer hover:bg-accent/10 flex items-center text-accent font-medium border-t border-gray-200"
                        >
                          + Add "{searchQuery.trim()}"
                        </div>
                      )}
                    </>
                  );
                })()
              )}
            </div>
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};

export default MultiSelect;
